/**
 * Parent half of the scene contract: ready/complete in, start/pause/resume/static out.
 * The inline bootstrap in inc/media-slot.php starts the frame and buffers messages,
 * because the scene reports "ready" before this script runs.
 */

const NS = 'interact:scene:';
const VERSION = 1;
const VISIBLE_RATIO = 0.25;

interface SceneMessage {
  type?: unknown;
  version?: unknown;
}

interface SceneRoot extends HTMLElement {
  phBuffer?: SceneMessage[];
  phOn?: (data: SceneMessage) => void;
  phLoaded?: number;
  phLoad?: () => void;
  phStart?: () => void;
  phActivate?: (mode?: ActivationMode) => void;
  phDeactivate?: () => void;
  phTogglePause?: () => boolean;
}

/** `replay` restarts the scene; `resume` continues where it paused. */
export type ActivationMode = 'replay' | 'resume';

/**
 * The scene's URL, wherever it ended up.
 *
 * NitroPack rewrites `data-src` to its own `nitro-lazy-src` and then never loads
 * the frame, so scenes ship on an attribute it ignores. The other two are read for
 * pages still served from its cache.
 */
function frameSource(frame: HTMLIFrameElement): string {
  return (
    frame.getAttribute('data-scene-src') ||
    frame.getAttribute('data-src') ||
    frame.getAttribute('nitro-lazy-src') ||
    ''
  );
}

function reveal(root: SceneRoot): void {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      root.dataset.sceneVisible = '1';
    });
  });
}

function setUp(root: SceneRoot, manual = false): void {
  const frame = root.querySelector<HTMLIFrameElement>('[class$="__scene-frame"]');

  if (!frame || root.dataset.sceneBound === '1') {
    return;
  }

  root.dataset.sceneBound = '1';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  // A standalone scene plays by itself and understands no commands.
  const frameEl = frame;

  if (root.dataset.sceneContract === 'standalone') {
    const show = () => {
      root.dataset.sceneState = 'standalone';
      reveal(root);
    };

    const showWhenLoaded = () => {
      if (root.phLoaded) {
        show();
      } else {
        root.phLoad = show;
      }
    };

    // A standalone scene takes no commands, so replay means reloading the frame.
    root.phActivate = (mode: ActivationMode = 'replay') => {
      const src = frameSource(frameEl);

      if (!src) {
        return;
      }

      // Resuming is impossible without messaging; only (re)loading is.
      if (mode === 'replay' || !frameEl.getAttribute('src')) {
        // A fresh src means a fresh load, so an earlier one must not satisfy the wait.
        root.phLoaded = 0;
        frameEl.src = src;
      }

      showWhenLoaded();
    };
    root.phDeactivate = () => {
      frameEl.removeAttribute('src');
      root.removeAttribute('data-scene-visible');
    };

    /*
     * A manual scene reveals on activation, never on load. Preloading sets the frame's
     * src early to warm the cache; revealing there would fade the fallback out behind a
     * closed panel, and the replay on opening would then show a blank frame reloading.
     */
    if (!manual) {
      showWhenLoaded();
      root.phActivate();
    }
    return;
  }

  let ready = false;
  /*
   * Two independent reasons a scene may not be watchable, kept apart on purpose:
   * the tab can be backgrounded without the element leaving the viewport, and the
   * IntersectionObserver does not re-fire on tab return. Folding both into one
   * flag let the tab hide it and nothing bring it back.
   */
  let inViewport = true;
  let tabVisible = !document.hidden;
  let started = false;
  let complete = false;
  let activated = !manual;
  let userPaused = false;

  // Opaque sandbox origin accepts no targetOrigin but "*"; the payload carries no data.
  const send = (command: string) => {
    frame.contentWindow?.postMessage({ type: NS + command, version: VERSION }, '*');
  };

  const sync = () => {
    if (!ready || complete) {
      return;
    }

    if (reduced.matches && !started) {
      send('static');
      root.dataset.sceneState = 'static';
      reveal(root);
      return;
    }

    if (!started) {
      if ((manual && !activated) || userPaused) {
        return;
      }

      started = true;
      root.dataset.sceneState = 'playing';
      send('start');
      reveal(root);
      return;
    }

    /*
     * Coming back restarts rather than resumes: the scene's own loop can be
     * mid-fade when it is paused, and resuming there leaves one cycle's closing
     * state on screen under the next cycle's opening state. An explicit viewer
     * pause outranks visibility and is never undone by scrolling.
     */
    const playing = inViewport && tabVisible && !userPaused;
    root.dataset.sceneState = playing ? 'playing' : 'paused';
    send(playing ? (reduced.matches ? 'static' : 'replay') : 'pause');
  };

  const receive = (data: SceneMessage) => {
    if (!data || typeof data !== 'object' || data.version !== VERSION) {
      return;
    }

    if (data.type === `${NS}ready`) {
      ready = true;
      sync();
    }

    if (data.type === `${NS}complete`) {
      complete = true;
      root.dataset.sceneState = 'complete';
    }
  };

  root.phActivate = (mode: ActivationMode = 'replay') => {
    activated = true;
    complete = false;
    inViewport = true;
    root.phStart?.();

    if (userPaused) {
      return;
    }

    if (started) {
      // The contract's own restart command; `start` would be a no-op mid-play.
      root.dataset.sceneState = 'playing';
      send(reduced.matches ? 'static' : mode === 'resume' ? 'resume' : 'replay');
      reveal(root);
      return;
    }

    sync();
  };

  root.phTogglePause = () => {
    userPaused = !userPaused;
    root.dataset.scenePaused = userPaused ? '1' : '0';

    if (started) {
      root.dataset.sceneState = userPaused ? 'paused' : 'playing';
      send(userPaused ? 'pause' : 'resume');
    }

    return userPaused;
  };

  root.phDeactivate = () => {
    activated = false;

    if (started) {
      send('pause');
      root.dataset.sceneState = 'paused';
    }
  };

  root.phOn = receive;
  const buffered = root.phBuffer ?? [];
  root.phBuffer = [];
  buffered.forEach(receive);

  if ('IntersectionObserver' in window) {
    new IntersectionObserver(
      (entries) => {
        inViewport = entries[0].isIntersecting;
        if (started) {
          sync();
        }
      },
      { threshold: VISIBLE_RATIO },
    ).observe(frame);
  }

  document.addEventListener('visibilitychange', () => {
    tabVisible = !document.hidden;
    if (started) {
      sync();
    }
  });

  // Only a back/forward restore needs this. A normal load fires pageshow too, and
  // re-syncing there would send `replay` to a scene that had just started.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) {
      return;
    }

    tabVisible = !document.hidden;
    if (started) {
      sync();
    }
  });

  reduced.addEventListener('change', sync);
}

export function initScenes(container: Document | HTMLElement = document): void {
  container
    .querySelectorAll<SceneRoot>('[data-interact-scene]')
    .forEach((root) => setUp(root, root.dataset.sceneActivation === 'manual'));
}

/** Play a manually activated scene; `replay` restarts, `resume` continues. */
export function activateScene(root: HTMLElement, mode: ActivationMode = 'replay'): void {
  (root as SceneRoot).phActivate?.(mode);
}

/** Toggle an explicit viewer pause; returns the new paused state. */
export function toggleScenePause(root: HTMLElement): boolean {
  return (root as SceneRoot).phTogglePause?.() ?? false;
}

/**
 * Load a manual scene's frame without revealing or playing it.
 *
 * Opening a panel then has nothing to download, so the swap from fallback image
 * to animation is immediate rather than a visible jump. A standalone scene is
 * replayed from cache on activation, so warming it costs only the transfer.
 */
export function warmScene(root: HTMLElement): void {
  (root as SceneRoot).phStart?.();
}

/** Pause a manually activated scene; standalone scenes unload instead. */
export function deactivateScene(root: HTMLElement): void {
  (root as SceneRoot).phDeactivate?.();
}

export function bootstrapScenes(): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initScenes(), { once: true });
  } else {
    initScenes();
  }
}
